import {
  InputTextField,
  Signature,
  SubmitComponentRenderProps
} from '@abbott-components/core';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { JSONSchema7 } from 'json-schema';
import { useState, useContext, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import ControlState from '../../Shared/controlState';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import { SignatureControls } from './SignatureControls';
import SignatureOverlay from './SignatureOverlay';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';

export default (control: ControlState) => {
  const properties = control.schema?.properties as JSONSchema7Properties;

  const consentSchema = properties['consent'] as JSONSchema7;

  const formContext = useFormContext();

  const fieldValueName = `${control.id}-value`;

  const fieldSignatureDateName = `${control.id}-date`;

  const labelSchema = properties['label'] as JSONSchema7;

  const fieldState = formContext?.getFieldState(fieldValueName);

  const initialSignatureData = formContext?.getValues(fieldValueName);

  const initialSignatureDate = formContext?.getValues(fieldSignatureDateName);

  const [showOverlay, setShowOverlay] = useState(true);

  const [areControlsDisabled, setAreControlsDisabled] = useState(true);

  const [isSignatureDisabled, setSignatureDisabled] = useState<boolean>(false);

  const { state } = useContext(WorkflowEngineContext);
  const { isReadOnlyView, mode } = state;

  const [signatureDate, setSignatureDate] =
    useState<string>(initialSignatureDate);

  const signatureContainerRef = useRef<HTMLDivElement | null>(null);
  const [signatureWidth, setSignatureWidth] = useState(0);

  const signatureHeight = 300;

  const readOnlySignatureHeight = '300px';
  const readOnlySignatureWidth = '100%';

  const renderHiddenFields = () => (
    <Stack sx={{ display: 'none' }}>
      {/* Hidden field used to capture signature value in form */}
      <InputTextField name={fieldValueName} />
      <InputTextField name={fieldSignatureDateName} />
    </Stack>
  );

  const renderSignatureDate = () =>
    signatureDate ? (
      <Typography>{`Signed ${new Date(
        signatureDate
      ).toDateString()} at ${new Date(
        signatureDate
      ).toLocaleTimeString()}`}</Typography>
    ) : null;

  const renderSignatureControls = ({
    clear,
    retrieveSigData
  }: SubmitComponentRenderProps) => (
    <SignatureControls
      consent={consentSchema.default as string}
      disabled={areControlsDisabled || isSignatureDisabled}
      onSubmit={() => {
        const signature = retrieveSigData() || '';
        const signedDate = new Date().toISOString();
        setAreControlsDisabled(true);
        setSignatureDate(signedDate);
        formContext?.setValue(fieldValueName, signature);
        formContext?.setValue(fieldSignatureDateName, signedDate);
        formContext?.clearErrors(fieldValueName);
      }}
      onClear={() => {
        clear();
        setAreControlsDisabled(true);
        formContext?.setValue(fieldValueName, '');
        formContext?.setValue(fieldSignatureDateName, '');
        formContext?.clearErrors(fieldValueName);
      }}
    />
  );

  const renderValidationErrors = () =>
    !!fieldState?.error && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fieldState?.error?.message}
      </Typography>
    );

  const renderSignature = () => (
    <>
      <Signature
        canvasOverlay={showOverlay && <SignatureOverlay />}
        canvasAttributes={{
          canvasProps: {
            width: signatureWidth,
            height: signatureHeight,
            style: {
              backgroundColor: '#5bc2e71D'
            }
          },
          onBegin: () => {
            setShowOverlay(isSignatureDisabled);
          },
          onEnd: () => {
            if (!signatureDate) {
              setAreControlsDisabled(false);
            }
          }
        }}
        SubmitComponent={renderSignatureControls}
      />
      {renderSignatureDate()}
      {renderValidationErrors()}
    </>
  );

  const renderReadOnlySignature = () => (
    <>
      <div
        style={{
          backgroundImage: `url(${initialSignatureData})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          width: `${readOnlySignatureWidth}`,
          height: `${readOnlySignatureHeight}`
        }}
        role="img"
        title={labelSchema?.default as string}
      ></div>
      {renderSignatureDate()}
    </>
  );

  useEffect(() => {
    setSignatureDisabled(isReadOnlyView);
  }, [isReadOnlyView, mode]);

  useEffect(() => {
    setSignatureWidth(signatureContainerRef.current?.offsetWidth || 0);
  }, [signatureContainerRef.current?.offsetWidth]);

  return (
    <div ref={signatureContainerRef} style={{ width: '100%' }}>
      {renderHiddenFields()}
      {initialSignatureData ? renderReadOnlySignature() : renderSignature()}
    </div>
  );
};
